//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Upload shape file dialog
//
//  Copyright:    © 2024 - 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   02.01.24 Sean Flook                  Initial Revision.
//    002   03.01.24 Sean Flook                  Fixed warning.
//    003   05.01.24 Sean Flook                  Use CSS shortcuts.
//    004   11.01.24 Sean Flook                  Fix warnings.
//    005   27.02.24 Sean Flook            MUL15 Fixed dialog title styling.
//    006   27.03.24 Sean Flook                  Added ADSDialogTitle.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    007   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//endregion Version 1.0.5.0
//region Version 1.0.7.0
//    008   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//    009   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    010   24.10.25 Sean Flook       IMANN-1129 Changed to allow for the uploading of shape .lyrx files used to style the shape files.
//    011   24.10.25 Sean Flook       IMANN-2097 Changes required to load georeferenced image files.
//    012   07.11.25 Sean Flook       IMANN-2097 Changes required to load georeferenced image file and world file.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";

import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Box,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import ADSTextControl from "../components/ADSTextControl";
import ADSMinMaxControl from "../components/ADSMinMaxControl";
import ADSSwitchControl from "../components/ADSSwitchControl";
import ADSSliderControl from "../components/ADSSliderControl";
import ADSReadOnlyControl from "../components/ADSReadOnlyControl";
import ADSDialogTitle from "../components/ADSDialogTitle";

import MinMaxDialog from "../dialogs/MinMaxDialog";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CancelIcon from "@mui/icons-material/Cancel";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import UploadIcon from "@mui/icons-material/Upload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import { adsDarkGrey, adsMidGreyA, adsMidGreyC } from "../utils/ADSColours";
import {
  blueButtonStyle,
  whiteButtonStyle,
  FormBoxRowStyle,
  FormRowStyle,
  controlLabelStyle,
  dragDropBoxStyle,
} from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";
import ADSCoordinateControl from "../components/ADSCoordinateControl";

UploadShpFileDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  currentIds: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
};

function UploadShpFileDialog({ isOpen, currentIds, onClose }) {
  const theme = useTheme();

  const [showDialog, setShowDialog] = useState(false);
  const [page, setPage] = useState(0);
  const [fileType, setFileType] = useState("shp");
  const [shpFile, setShpFile] = useState(null);
  const [shpLayerFile, setShpLayerFile] = useState(null);
  const [geometryType, setGeometryType] = useState("");
  const [geoFile, setGeoFile] = useState(null);
  const [geoWorldFile, setGeoWorldFile] = useState(null);

  const [title, setTitle] = useState("");
  const [layerId, setLayerId] = useState("");
  const [copyright, setCopyright] = useState("");
  const [displayInList, setDisplayInList] = useState(true);
  const [maxScale, setMaxScale] = useState(0);
  const [minScale, setMinScale] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [esuSnap, setEsuSnap] = useState(false);
  const [blpuSnap, setBlpuSnap] = useState(false);
  const [extentSnap, setExtentSnap] = useState(false);
  const [xMin, setXMin] = useState(0);
  const [yMin, setYMin] = useState(0);
  const [xMax, setXMax] = useState(0);
  const [yMax, setYMax] = useState(0);

  const [layerIdError, setLayerIdError] = useState(null);

  const [showMinMaxDialog, setShowMinMaxDialog] = useState(false);
  const [minMaxType, setMinMaxType] = useState("scale");
  const minValue = useRef(0);
  const maxValue = useRef(0);
  const [maximum, setMaximum] = useState(23);
  const [acceptedFileTypes, setAcceptedFileTypes] = useState({ "application/x-zip-compressed": [".zip"] });
  const [uploadMaxFiles, setUploadMaxFiles] = useState(1);

  const currentIdsRef = useRef(currentIds);

  /** Event to handle when files are dropped */
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        const fileName = file.name.toLowerCase();
        if (!file) continue;
        if (fileName.endsWith(".zip")) {
          setShpFile(file);
          if (!currentIdsRef.current.includes(fileName.replace(".zip", ""))) {
            setLayerId(fileName.replace(".zip", "").replaceAll(" ", "_"));
          }
          // } else if (file.name.endsWith(".lyrx") || file.name.endsWith(".qml")) {
        } else if (fileName.endsWith(".lyrx")) {
          setShpLayerFile(file);
        } else {
          if (file.type === "image/*" || file.type.startsWith("image/")) {
            setGeoFile(file);

            switch (file.type) {
              case "image/apng":
                if (fileName.endsWith(".apng") && !currentIdsRef.current.includes(fileName.replace(".apng", ""))) {
                  setLayerId(fileName.replace(".apng", "").replaceAll(" ", "_"));
                } else if (fileName.endsWith(".png") && !currentIdsRef.current.includes(fileName.replace(".png", ""))) {
                  setLayerId(fileName.replace(".png", "").replaceAll(" ", "_"));
                }
                break;

              case "image/avif":
                if (fileName.endsWith(".avif") && !currentIdsRef.current.includes(fileName.replace(".avif", ""))) {
                  setLayerId(fileName.replace(".avif", "").replaceAll(" ", "_"));
                }
                break;

              case "image/gif":
                if (fileName.endsWith(".gif") && !currentIdsRef.current.includes(fileName.replace(".gif", ""))) {
                  setLayerId(fileName.replace(".gif", "").replaceAll(" ", "_"));
                }
                break;

              case "image/jpeg":
                if (fileName.endsWith(".jpeg") && !currentIdsRef.current.includes(fileName.replace(".jpeg", ""))) {
                  setLayerId(fileName.replace(".jpeg", "").replaceAll(" ", "_"));
                } else if (fileName.endsWith(".jpg") && !currentIdsRef.current.includes(fileName.replace(".jpg", ""))) {
                  setLayerId(fileName.replace(".jpg", "").replaceAll(" ", "_"));
                } else if (
                  fileName.endsWith(".jfif") &&
                  !currentIdsRef.current.includes(fileName.replace(".jfif", ""))
                ) {
                  setLayerId(fileName.replace(".jfif", "").replaceAll(" ", "_"));
                } else if (
                  fileName.endsWith(".pjpeg") &&
                  !currentIdsRef.current.includes(fileName.replace(".pjpeg", ""))
                ) {
                  setLayerId(fileName.replace(".pjpeg", "").replaceAll(" ", "_"));
                } else if (fileName.endsWith(".pjp") && !currentIdsRef.current.includes(fileName.replace(".pjp", ""))) {
                  setLayerId(fileName.replace(".pjp", "").replaceAll(" ", "_"));
                }
                break;

              case "image/png":
                if (fileName.endsWith(".png") && !currentIdsRef.current.includes(fileName.replace(".png", ""))) {
                  setLayerId(fileName.replace(".png", "").replaceAll(" ", "_"));
                }
                break;

              case "image/svg+xml":
                if (fileName.endsWith(".svg") && !currentIdsRef.current.includes(fileName.replace(".svg", ""))) {
                  setLayerId(fileName.replace(".svg", "").replaceAll(" ", "_"));
                } else if (
                  fileName.endsWith(".svgz") &&
                  !currentIdsRef.current.includes(fileName.replace(".svgz", ""))
                ) {
                  setLayerId(fileName.replace(".svgz", "").replaceAll(" ", "_"));
                }
                break;

              case "image/webp":
                if (fileName.endsWith(".webp") && !currentIdsRef.current.includes(fileName.replace(".webp", "")))
                  setLayerId(fileName.replace(".webp", "").replaceAll(" ", "_"));
                break;

              case "image/bmp":
                if (fileName.endsWith(".bmp") && !currentIdsRef.current.includes(fileName.replace(".bmp", ""))) {
                  setLayerId(fileName.replace(".bmp", "").replaceAll(" ", "_"));
                } else if (fileName.endsWith(".dib") && !currentIdsRef.current.includes(fileName.replace(".dib", ""))) {
                  setLayerId(fileName.replace(".dib", "").replaceAll(" ", "_"));
                }
                break;

              case "image/tiff":
                if (fileName.endsWith(".tiff") && !currentIdsRef.current.includes(fileName.replace(".tiff", ""))) {
                  setLayerId(fileName.replace(".tiff", "").replaceAll(" ", "_"));
                } else if (fileName.endsWith(".tif") && !currentIdsRef.current.includes(fileName.replace(".tif", ""))) {
                  setLayerId(fileName.replace(".tif", "").replaceAll(" ", "_"));
                }
                break;

              default:
                break;
            }
          } else if (
            fileName.endsWith(".wld") ||
            fileName.endsWith(".apngw") ||
            fileName.endsWith(".avifw") ||
            fileName.endsWith(".gifw") ||
            fileName.endsWith(".jpegw") ||
            fileName.endsWith(".jpgw") ||
            fileName.endsWith(".jfifw") ||
            fileName.endsWith(".pjpegw") ||
            fileName.endsWith(".pjpw") ||
            fileName.endsWith(".pngw") ||
            fileName.endsWith(".svgw") ||
            fileName.endsWith(".svgzw") ||
            fileName.endsWith(".webpw") ||
            fileName.endsWith(".bmpw") ||
            fileName.endsWith(".dibw") ||
            fileName.endsWith(".tiffw") ||
            fileName.endsWith(".tifw") ||
            fileName.endsWith(".apngwx") ||
            fileName.endsWith(".avifwx") ||
            fileName.endsWith(".gifwx") ||
            fileName.endsWith(".jpegwx") ||
            fileName.endsWith(".jpgwx") ||
            fileName.endsWith(".jfifwx") ||
            fileName.endsWith(".pjpegwx") ||
            fileName.endsWith(".pjpwx") ||
            fileName.endsWith(".pngwx") ||
            fileName.endsWith(".svgwx") ||
            fileName.endsWith(".svgzwx") ||
            fileName.endsWith(".webpwx") ||
            fileName.endsWith(".bmpwx") ||
            fileName.endsWith(".dibwx") ||
            fileName.endsWith(".tiffwx") ||
            fileName.endsWith(".tifwx") ||
            fileName.endsWith(".agw") ||
            fileName.endsWith(".afw") ||
            fileName.endsWith(".gfw") ||
            fileName.endsWith(".jgw") ||
            fileName.endsWith(".jfw") ||
            fileName.endsWith(".pgw") ||
            fileName.endsWith(".ppw") ||
            fileName.endsWith(".sgw") ||
            fileName.endsWith(".szw") ||
            fileName.endsWith(".wpw") ||
            fileName.endsWith(".bpw") ||
            fileName.endsWith(".dbw") ||
            fileName.endsWith(".tfw") ||
            fileName.endsWith(".agwx") ||
            fileName.endsWith(".afwx") ||
            fileName.endsWith(".gfwx") ||
            fileName.endsWith(".jgwx") ||
            fileName.endsWith(".jfwx") ||
            fileName.endsWith(".pgwx") ||
            fileName.endsWith(".ppwx") ||
            fileName.endsWith(".sgwx") ||
            fileName.endsWith(".szwx") ||
            fileName.endsWith(".wpwx") ||
            fileName.endsWith(".bpwx") ||
            fileName.endsWith(".dbwx") ||
            fileName.endsWith(".tfwx")
          ) {
            setGeoWorldFile(file);
          }
        }
      }
    } else {
      setShpFile(null);
      setShpLayerFile(null);
      setGeoFile(null);
      setGeoWorldFile(null);
    }
  }, []);

  /** Dropzone configuration */
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    maxFiles: uploadMaxFiles,
  });

  /** Event to handle removing the zip file. */
  const handleRemoveZipFile = () => {
    setShpFile(null);
    setLayerId("");
  };

  /** Event to handle removing the layer file. */
  const handleRemoveLayerFile = () => {
    setShpLayerFile(null);
  };

  /** Event to handle removing the geo file. */
  const handleRemoveGeoFile = () => {
    setGeoFile(null);
    setLayerId("");
  };

  /** Event to handle removing the world file. */
  const handleRemoveGeoWorldFile = () => {
    setGeoWorldFile(null);
  };

  /**
   * Event to handle when the file type changes.
   *
   * @param {object} event The event object.
   */
  const handleFileTypeChangeEvent = (event) => {
    setFileType(event.target.value);
    // Reset to the initial values
    setShpFile(null);
    setShpLayerFile(null);
    setGeometryType("");
    setGeoFile(null);
    setGeoWorldFile(null);
    setTitle("");
    setLayerId("");
    setCopyright("");
    setDisplayInList(true);
    setMaxScale(0);
    setMinScale(0);
    setOpacity(1);
    setEsuSnap(false);
    setBlpuSnap(false);
    setExtentSnap(false);
    setXMin(0);
    setYMin(0);
    setXMax(0);
    setYMax(0);
  };

  /**
   * Event to handle when the continue button is clicked
   */
  const handleContinueClick = () => {
    switch (fileType) {
      case "shp":
        setAcceptedFileTypes({ "application/x-zip-compressed": [".zip"] });
        setUploadMaxFiles(1);
        setPage(1);
        break;
      case "shplayer":
        // setAcceptedFileTypes({ "application/x-zip-compressed": [".zip", ".lyrx", ".qml"] });
        setAcceptedFileTypes({ "application/x-zip-compressed": [".zip", ".lyrx"] });
        setUploadMaxFiles(2);
        setPage(2);
        break;
      case "geo":
        setAcceptedFileTypes({ "image/*": [] });
        setUploadMaxFiles(1);
        setPage(3);
        break;
      case "geoworld":
        setAcceptedFileTypes({
          "image/*": [
            ".apng",
            ".avif",
            ".gif",
            ".jpeg",
            ".jpg",
            ".jfif",
            ".pjpeg",
            ".pjp",
            ".png",
            ".svg",
            ".svgz",
            ".webp",
            ".bmp",
            ".dib",
            ".tiff",
            ".tif",
            ".wld",
            ".apngw",
            ".avifw",
            ".gifw",
            ".jpegw",
            ".jpgw",
            ".jfifw",
            ".pjpegw",
            ".pjpw",
            ".pngw",
            ".svgw",
            ".svgzw",
            ".webpw",
            ".bmpw",
            ".dibw",
            ".tiffw",
            ".tifw",
            ".apngwx",
            ".avifwx",
            ".gifwx",
            ".jpegwx",
            ".jpgwx",
            ".jfifwx",
            ".pjpegwx",
            ".pjpwx",
            ".pngwx",
            ".svgwx",
            ".svgzwx",
            ".webpwx",
            ".bmpwx",
            ".dibwx",
            ".tiffwx",
            ".tifwx",
            ".agw",
            ".afw",
            ".gfw",
            ".jgw",
            ".jfw",
            ".pgw",
            ".ppw",
            ".sgw",
            ".szw",
            ".wpw",
            ".bpw",
            ".dbw",
            ".tfw",
            ".agwx",
            ".afwx",
            ".gfwx",
            ".jgwx",
            ".jfwx",
            ".pgwx",
            ".ppwx",
            ".sgwx",
            ".szwx",
            ".wpwx",
            ".bpwx",
            ".dbwx",
            ".tfwx",
          ],
        });
        setUploadMaxFiles(2);
        setPage(4);
        break;
      default:
        setPage(0);
        break;
    }
  };

  /**
   * Event to handle when the previous button is clicked
   */
  const handlePreviousClick = () => {
    setPage(0);
  };

  /**
   * Event to handle when the close button is clicked
   */
  const handleUploadClick = () => {
    if (
      ((fileType === "shp" && shpFile) ||
        (fileType === "shplayer" && shpFile && shpLayerFile) ||
        (fileType === "geo" && geoFile) ||
        (fileType === "geoworld" && geoFile && geoWorldFile)) &&
      onClose &&
      !currentIdsRef.current.includes(layerId)
    ) {
      const returnData = {
        type: fileType,
        file: fileType === "shp" || fileType === "shplayer" ? shpFile : geoFile,
        layerFile: fileType === "shplayer" ? shpLayerFile : null,
        worldFile: fileType === "geoworld" ? geoWorldFile : null,
        geometryType: geometryType,
        title: title,
        layerId: layerId,
        copyright: copyright,
        displayInList: displayInList,
        maxScale: maxScale,
        minScale: minScale,
        opacity: opacity,
        esuSnap: esuSnap,
        blpuSnap: blpuSnap,
        extentSnap: extentSnap,
        xMin: xMin,
        yMin: yMin,
        xMax: xMax,
        yMax: yMax,
      };
      onClose(returnData);
    }
  };

  /**
   * Event to handle when the close button is clicked
   */
  const handleCancelClick = () => {
    if (onClose) onClose(null);
  };

  /**
   * Event to handle when the title changes.
   *
   * @param {string} newValue The new title.
   */
  const handleTitleChangeEvent = (newValue) => {
    setTitle(newValue);
  };

  /**
   * Event to handle when the layer id changes.
   *
   * @param {number} newValue The new layer id.
   */
  const handleLayerIdChangeEvent = (newValue) => {
    setLayerId(newValue.replaceAll(" ", "_"));
    if (currentIdsRef.current.includes(newValue)) {
      setLayerIdError(`${newValue} is not unique.`);
    } else setLayerIdError(null);
  };

  /**
   * Event to handle when the copyright information changes.
   *
   * @param {string} newValue The new copyright information.
   */
  const handleCopyrightInformationChangeEvent = (newValue) => {
    setCopyright(newValue);
  };

  /**
   * Event to handle when the display in list flag changes.
   *
   * @param {boolean} newValue The new display in list flag.
   */
  const handleDisplayInListChangeEvent = (newValue) => {
    setDisplayInList(newValue);
  };

  /**
   * Event to handle when the minimum and maximum scale changes.
   */
  const handleMinMaxScaleChangeEvent = () => {
    setMinMaxType("scale");
    minValue.current = minScale ? minScale : 0;
    maxValue.current = maxScale ? maxScale : 0;
    setMaximum(23);
    setShowMinMaxDialog(true);
  };

  /**
   * Event to handle when the minimum and maximum scale changes.
   *
   * @param {object} minMaxData The new minimum and maximum data.
   */
  const handleNewMinMax = (minMaxData) => {
    if (minMaxData) {
      minValue.current = minMaxData.min;
      maxValue.current = minMaxData.max;

      setMinScale(minMaxData.min);
      setMaxScale(minMaxData.max);
    }

    setShowMinMaxDialog(false);
  };

  /**
   * Event to handle the closing of the Min/Max dialog.
   */
  const handleCloseMinMax = () => {
    setShowMinMaxDialog(false);
  };

  /**
   * Event to handle when the opacity changes.
   *
   * @param {number} newValue The new opacity.
   */
  const handleOpacityChangeEvent = (newValue) => {
    setOpacity(newValue);
  };

  /**
   * Event to handle when the ESU snap flag changes.
   *
   * @param {boolean} newValue The new ESU snap flag.
   */
  const handleEsuSnapChangeEvent = (newValue) => {
    setEsuSnap(newValue);
  };

  /**
   * Event to handle when the BLPU snap flag changes.
   *
   * @param {boolean} newValue The new BLPU snap flag.
   */
  const handleBlpuSnapChangeEvent = (newValue) => {
    setBlpuSnap(newValue);
  };

  /**
   * Event to handle when the extent snap flag changes.
   *
   * @param {boolean} newValue The new extent snap flag.
   */
  const handleExtentSnapChangeEvent = (newValue) => {
    setExtentSnap(newValue);
  };

  /**
   * Event to handle when the X minimum coordinate changes.
   *
   * @param {number} newValue The new X minimum coordinate.
   */
  const handleXMinChangeEvent = (newValue) => {
    setXMin(newValue);
  };

  /**
   * Event to handle when the Y minimum coordinate changes.
   *
   * @param {number} newValue The new Y minimum coordinate.
   */
  const handleYMinChangeEvent = (newValue) => {
    setYMin(newValue);
  };

  /**
   * Event to handle when the X maximum coordinate changes.
   *
   * @param {number} newValue The new X maximum coordinate.
   */
  const handleXMaxChangeEvent = (newValue) => {
    setXMax(newValue);
  };

  /**
   * Event to handle when the Y maximum coordinate changes.
   *
   * @param {number} newValue The new Y maximum coordinate.
   */
  const handleYMaxChangeEvent = (newValue) => {
    setYMax(newValue);
  };

  /**
   * Get the title for the dialog.
   *
   * @returns {string} The dialog title.
   */
  const getTitle = () => {
    switch (page) {
      case 0:
        return "Select upload file type";
      case 1:
        return "Load shape file";
      case 2:
        return "Load shape & layer file";
      case 3:
        return "Load image file";
      case 4:
        return "Load image and world file";
      default:
        return "Select upload file type";
    }
  };

  /**
   * Get the content for the dialog.
   *
   * @returns {JSX.Element|string} The dialog content.
   */
  const getContent = () => {
    switch (page) {
      case 0:
        return (
          <Stack
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <FormControl>
              <FormLabel
                id="file-type-group-label"
                sx={{ fontFamily: "Nunito sans", fontSize: "18px", color: adsDarkGrey }}
              >
                Select the type of file you wish to upload:
              </FormLabel>
              <Stack
                direction="column"
                spacing={2}
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <RadioGroup
                  aria-labelledby="file-type-group-label"
                  name="file-type-group"
                  value={fileType}
                  sx={{ mt: "8px" }}
                  onChange={handleFileTypeChangeEvent}
                >
                  <FormControlLabel value="shp" control={<Radio />} label="Shape file (using default symbology)" />
                  <FormControlLabel
                    value="shplayer"
                    control={<Radio />}
                    label="Shape file (using symbology from a layer file)"
                  />
                  <FormControlLabel value="geo" control={<Radio />} label="Image file (manual georeferencing)" />
                  <FormControlLabel
                    value="geoworld"
                    control={<Radio />}
                    label="Image file (georeferenced by a world file)"
                  />
                </RadioGroup>
              </Stack>
            </FormControl>
          </Stack>
        );
      case 1:
        return (
          <Grid container justifyContent="flex-start" spacing={0}>
            <Grid size={12}>
              <Stack direction="column" spacing={0}>
                {!shpFile && (
                  <Box
                    {...getRootProps({
                      sx: dragDropBoxStyle(isDragActive),
                    })}
                  >
                    <input {...getInputProps()} />
                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}
                    >
                      {isDragActive ? (
                        <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
                          <CloudUploadIcon sx={{ color: adsMidGreyC }} />
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "Nunito sans", fontSize: "14px", color: adsMidGreyA }}
                          >
                            Drop the zip file here ...
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "Nunito sans", fontSize: "14px", color: adsMidGreyA }}
                        >
                          Drag 'n' drop zip file here, or click to select the zip file
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                {shpFile && (
                  <ADSReadOnlyControl
                    label="Zip file*"
                    value={shpFile ? shpFile.name : ""}
                    buttonVariant="removeFile"
                    onButtonClick={handleRemoveZipFile}
                  />
                )}
                <Box sx={FormBoxRowStyle(false)}>
                  <Grid container justifyContent="flex-start" alignItems={"center"} sx={FormRowStyle(false, false)}>
                    <Grid size={3}>
                      <Typography
                        id={`ads-text-label-geometry-type`}
                        variant="body2"
                        align="left"
                        sx={controlLabelStyle}
                      >
                        Geometry type*
                      </Typography>
                    </Grid>
                    <Grid size={9}>
                      <RadioGroup
                        row
                        disabled={!shpFile}
                        name="geometry-type-group"
                        value={geometryType}
                        onChange={(e) => setGeometryType(e.target.value)}
                      >
                        <FormControlLabel value="point" control={<Radio />} label="Point" />
                        <FormControlLabel value="line" control={<Radio />} label="Line" />
                        <FormControlLabel value="polygon" control={<Radio />} label="Polygon" />
                      </RadioGroup>
                    </Grid>
                  </Grid>
                </Box>
                <ADSTextControl
                  label="Title"
                  isEditable={shpFile}
                  isRequired
                  maxLength={40}
                  value={title}
                  id="layer_title"
                  helperText="The title of the layer used to identify it in the layer control."
                  onChange={handleTitleChangeEvent}
                />
                <ADSTextControl
                  label="Map id"
                  isEditable={shpFile}
                  maxLength={50}
                  isRequired
                  value={layerId}
                  id="layer_id"
                  characterSet="EsriLayerId"
                  helperText="The unique ID assigned to the layer."
                  errorText={layerIdError}
                  onChange={handleLayerIdChangeEvent}
                />
                <ADSTextControl
                  label="Copyright information"
                  isEditable={shpFile}
                  maxLength={100}
                  value={copyright}
                  id="layer_copyright_information"
                  helperText="The copyright information that needs to be displayed on the map. If the current year needs to be displayed use <<year>>."
                  onChange={handleCopyrightInformationChangeEvent}
                />
                <ADSSwitchControl
                  label="Display in layer control"
                  isEditable={shpFile}
                  checked={displayInList}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want this layer to be displayed in the layer control."
                  onChange={handleDisplayInListChangeEvent}
                />
                <ADSMinMaxControl
                  label="Scale range"
                  isEditable={shpFile}
                  minValue={minScale}
                  maxValue={maxScale}
                  helperText="The minimum scale (most zoomed out) and maximum scale (most zoomed in) at which the layer is visible in the map."
                  onChange={handleMinMaxScaleChangeEvent}
                />
                <ADSSliderControl
                  label="Opacity"
                  narrowLabel
                  isEditable={shpFile}
                  includeLabel
                  min={0}
                  max={1}
                  step={0.01}
                  helperText="The opacity of the layer in the map where 0 is transparent and 1 is opaque."
                  value={opacity}
                  onChange={handleOpacityChangeEvent}
                />
                <ADSSwitchControl
                  label="ESU snap to layer"
                  isEditable={shpFile}
                  checked={esuSnap}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want to use this layer for snapping ESU nodes to it."
                  onChange={handleEsuSnapChangeEvent}
                />
                <ADSSwitchControl
                  label="BLPU snap to layer"
                  isEditable={shpFile}
                  checked={blpuSnap}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want to use this layer for snapping BLPUs to it."
                  onChange={handleBlpuSnapChangeEvent}
                />
                <ADSSwitchControl
                  label="Extent snap to layer"
                  isEditable={shpFile}
                  checked={extentSnap}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want to use this layer for snapping BLPU extents to it."
                  onChange={handleExtentSnapChangeEvent}
                />
              </Stack>
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container justifyContent="flex-start" spacing={0}>
            <Grid size={12}>
              <Stack direction="column" spacing={0}>
                {(!shpFile || !shpLayerFile) && (
                  <Box
                    {...getRootProps({
                      sx: dragDropBoxStyle(isDragActive),
                    })}
                  >
                    <input {...getInputProps()} />
                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}
                    >
                      {isDragActive ? (
                        <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
                          <CloudUploadIcon sx={{ color: adsMidGreyC }} />
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "Nunito sans", fontSize: "14px", color: adsMidGreyA }}
                          >
                            Drop the files here ...
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "Nunito sans", fontSize: "14px", color: adsMidGreyA }}
                        >
                          Drag 'n' drop files here, or click to select the files
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                {shpFile && (
                  <ADSReadOnlyControl
                    label="Zip file*"
                    value={shpFile ? shpFile.name : ""}
                    buttonVariant="removeFile"
                    onButtonClick={handleRemoveZipFile}
                  />
                )}
                {shpLayerFile && (
                  <ADSReadOnlyControl
                    label="Layer file*"
                    value={shpLayerFile ? shpLayerFile.name : ""}
                    buttonVariant="removeFile"
                    onButtonClick={handleRemoveLayerFile}
                  />
                )}
                <ADSTextControl
                  label="Title"
                  isEditable={shpFile && shpLayerFile}
                  isRequired
                  maxLength={40}
                  value={title}
                  id="layer_title"
                  helperText="The title of the layer used to identify it in the layer control."
                  onChange={handleTitleChangeEvent}
                />
                <ADSTextControl
                  label="Map id"
                  isEditable={shpFile && shpLayerFile}
                  maxLength={50}
                  isRequired
                  value={layerId}
                  id="layer_id"
                  characterSet="EsriLayerId"
                  helperText="The unique ID assigned to the layer."
                  errorText={layerIdError}
                  onChange={handleLayerIdChangeEvent}
                />
                <ADSTextControl
                  label="Copyright information"
                  isEditable={shpFile && shpLayerFile}
                  maxLength={100}
                  value={copyright}
                  id="layer_copyright_information"
                  helperText="The copyright information that needs to be displayed on the map. If the current year needs to be displayed use <<year>>."
                  onChange={handleCopyrightInformationChangeEvent}
                />
                <ADSSwitchControl
                  label="Display in layer control"
                  isEditable={shpFile && shpLayerFile}
                  checked={displayInList}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want this layer to be displayed in the layer control."
                  onChange={handleDisplayInListChangeEvent}
                />
                <ADSMinMaxControl
                  label="Scale range"
                  isEditable={shpFile && shpLayerFile}
                  minValue={minScale}
                  maxValue={maxScale}
                  helperText="The minimum scale (most zoomed out) and maximum scale (most zoomed in) at which the layer is visible in the map."
                  onChange={handleMinMaxScaleChangeEvent}
                />
                <ADSSliderControl
                  label="Opacity"
                  narrowLabel
                  isEditable={shpFile && shpLayerFile}
                  includeLabel
                  min={0}
                  max={1}
                  step={0.01}
                  helperText="The opacity of the layer in the map where 0 is transparent and 1 is opaque."
                  value={opacity}
                  onChange={handleOpacityChangeEvent}
                />
                <ADSSwitchControl
                  label="ESU snap to layer"
                  isEditable={shpFile && shpLayerFile}
                  checked={esuSnap}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want to use this layer for snapping ESU nodes to it."
                  onChange={handleEsuSnapChangeEvent}
                />
                <ADSSwitchControl
                  label="BLPU snap to layer"
                  isEditable={shpFile && shpLayerFile}
                  checked={blpuSnap}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want to use this layer for snapping BLPUs to it."
                  onChange={handleBlpuSnapChangeEvent}
                />
                <ADSSwitchControl
                  label="Extent snap to layer"
                  isEditable={shpFile && shpLayerFile}
                  checked={extentSnap}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want to use this layer for snapping BLPU extents to it."
                  onChange={handleExtentSnapChangeEvent}
                />
              </Stack>
            </Grid>
          </Grid>
        );
      case 3:
        return (
          <Grid container justifyContent="flex-start" spacing={0}>
            <Grid size={12}>
              <Stack direction="column" spacing={0}>
                {!geoFile && (
                  <Box
                    {...getRootProps({
                      sx: dragDropBoxStyle(isDragActive),
                    })}
                  >
                    <input {...getInputProps()} />
                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}
                    >
                      {isDragActive ? (
                        <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
                          <CloudUploadIcon sx={{ color: adsMidGreyC }} />
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "Nunito sans", fontSize: "14px", color: adsMidGreyA }}
                          >
                            Drop the image file here ...
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "Nunito sans", fontSize: "14px", color: adsMidGreyA }}
                        >
                          Drag 'n' drop image file here, or click to select the file
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                {geoFile && (
                  <ADSReadOnlyControl
                    label="Image file*"
                    value={geoFile ? geoFile.name : ""}
                    buttonVariant="removeFile"
                    onButtonClick={handleRemoveGeoFile}
                  />
                )}
                <ADSCoordinateControl
                  label="Image georeference extent"
                  isEditable={!!geoFile}
                  isRequired
                  helperText="The lower left of the extent for the image."
                  eastValue={Number(xMin)}
                  northValue={Number(yMin)}
                  eastLabel="min X:"
                  northLabel="min Y:"
                  onEastChange={handleXMinChangeEvent}
                  onNorthChange={handleYMinChangeEvent}
                />
                <ADSCoordinateControl
                  label=""
                  isEditable={!!geoFile}
                  isRequired
                  helperText="The upper right of the extent for the image."
                  eastValue={Number(xMax)}
                  northValue={Number(yMax)}
                  eastLabel="max X:"
                  northLabel="max Y:"
                  onEastChange={handleXMaxChangeEvent}
                  onNorthChange={handleYMaxChangeEvent}
                />
                <ADSTextControl
                  label="Title"
                  isEditable={!!geoFile}
                  isRequired
                  maxLength={40}
                  value={title}
                  id="layer_title"
                  helperText="The title of the layer used to identify it in the layer control."
                  onChange={handleTitleChangeEvent}
                />
                <ADSTextControl
                  label="Map id"
                  isEditable={!!geoFile}
                  maxLength={50}
                  isRequired
                  value={layerId}
                  id="layer_id"
                  characterSet="EsriLayerId"
                  helperText="The unique ID assigned to the layer."
                  errorText={layerIdError}
                  onChange={handleLayerIdChangeEvent}
                />
                <ADSTextControl
                  label="Copyright information"
                  isEditable={!!geoFile}
                  maxLength={100}
                  value={copyright}
                  id="layer_copyright_information"
                  helperText="The copyright information that needs to be displayed on the map. If the current year needs to be displayed use <<year>>."
                  onChange={handleCopyrightInformationChangeEvent}
                />
                <ADSSwitchControl
                  label="Display in layer control"
                  isEditable={!!geoFile}
                  checked={displayInList}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want this layer to be displayed in the layer control."
                  onChange={handleDisplayInListChangeEvent}
                />
                <ADSMinMaxControl
                  label="Scale range"
                  isEditable={!!geoFile}
                  minValue={minScale}
                  maxValue={maxScale}
                  helperText="The minimum scale (most zoomed out) and maximum scale (most zoomed in) at which the layer is visible in the map."
                  onChange={handleMinMaxScaleChangeEvent}
                />
                <ADSSliderControl
                  label="Opacity"
                  narrowLabel
                  isEditable={!!geoFile}
                  includeLabel
                  min={0}
                  max={1}
                  step={0.01}
                  helperText="The opacity of the layer in the map where 0 is transparent and 1 is opaque."
                  value={opacity}
                  onChange={handleOpacityChangeEvent}
                />
              </Stack>
            </Grid>
          </Grid>
        );
      case 4:
        return (
          <Grid container justifyContent="flex-start" spacing={0}>
            <Grid size={12}>
              <Stack direction="column" spacing={0}>
                {(!geoFile || !geoWorldFile) && (
                  <Box
                    {...getRootProps({
                      sx: dragDropBoxStyle(isDragActive),
                    })}
                  >
                    <input {...getInputProps()} />
                    <Stack
                      direction="column"
                      spacing={1}
                      sx={{ height: "100%", justifyContent: "center", alignItems: "center" }}
                    >
                      {isDragActive ? (
                        <Stack direction="row" spacing={1} sx={{ justifyContent: "center", alignItems: "center" }}>
                          <CloudUploadIcon sx={{ color: adsMidGreyC }} />
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "Nunito sans", fontSize: "14px", color: adsMidGreyA }}
                          >
                            Drop the files here ...
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "Nunito sans", fontSize: "14px", color: adsMidGreyA }}
                        >
                          Drag 'n' drop files here, or click to select the files
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                )}
                {geoFile && (
                  <ADSReadOnlyControl
                    label="Image file*"
                    value={geoFile ? geoFile.name : ""}
                    buttonVariant="removeFile"
                    onButtonClick={handleRemoveGeoFile}
                  />
                )}
                {geoWorldFile && (
                  <ADSReadOnlyControl
                    label="World file*"
                    value={geoWorldFile ? geoWorldFile.name : ""}
                    buttonVariant="removeFile"
                    onButtonClick={handleRemoveGeoWorldFile}
                  />
                )}
                <ADSTextControl
                  label="Title"
                  isEditable={geoFile && geoWorldFile}
                  isRequired
                  maxLength={40}
                  value={title}
                  id="layer_title"
                  helperText="The title of the layer used to identify it in the layer control."
                  onChange={handleTitleChangeEvent}
                />
                <ADSTextControl
                  label="Map id"
                  isEditable={geoFile && geoWorldFile}
                  maxLength={50}
                  isRequired
                  value={layerId}
                  id="layer_id"
                  characterSet="EsriLayerId"
                  helperText="The unique ID assigned to the layer."
                  errorText={layerIdError}
                  onChange={handleLayerIdChangeEvent}
                />
                <ADSTextControl
                  label="Copyright information"
                  isEditable={geoFile && geoWorldFile}
                  maxLength={100}
                  value={copyright}
                  id="layer_copyright_information"
                  helperText="The copyright information that needs to be displayed on the map. If the current year needs to be displayed use <<year>>."
                  onChange={handleCopyrightInformationChangeEvent}
                />
                <ADSSwitchControl
                  label="Display in layer control"
                  isEditable={geoFile && geoWorldFile}
                  checked={displayInList}
                  trueLabel="Yes"
                  falseLabel="No"
                  helperText="Set this if you want this layer to be displayed in the layer control."
                  onChange={handleDisplayInListChangeEvent}
                />
                <ADSMinMaxControl
                  label="Scale range"
                  isEditable={geoFile && geoWorldFile}
                  minValue={minScale}
                  maxValue={maxScale}
                  helperText="The minimum scale (most zoomed out) and maximum scale (most zoomed in) at which the layer is visible in the map."
                  onChange={handleMinMaxScaleChangeEvent}
                />
                <ADSSliderControl
                  label="Opacity"
                  narrowLabel
                  isEditable={geoFile && geoWorldFile}
                  includeLabel
                  min={0}
                  max={1}
                  step={0.01}
                  helperText="The opacity of the layer in the map where 0 is transparent and 1 is opaque."
                  value={opacity}
                  onChange={handleOpacityChangeEvent}
                />
              </Stack>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  /**
   * Get the actions for the dialog.
   *
   * @returns {JSX.Element|string} The dialog actions.
   */
  const getActions = () => {
    switch (page) {
      case 0:
        return (
          <>
            <Button
              variant="contained"
              onClick={handleContinueClick}
              autoFocus
              disabled={!fileType}
              sx={blueButtonStyle}
              startIcon={<NavigateNextIcon />}
            >
              Next
            </Button>
            <Button variant="contained" onClick={handleCancelClick} sx={whiteButtonStyle} startIcon={<CancelIcon />}>
              Cancel
            </Button>
          </>
        );
      case 1:
        return (
          <>
            <Button
              variant="contained"
              onClick={handlePreviousClick}
              sx={whiteButtonStyle}
              startIcon={<NavigateBeforeIcon />}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadClick}
              autoFocus
              disabled={
                !shpFile ||
                !["application/zip", "application/octet-stream", "application/x-zip-compressed"].includes(
                  shpFile.type
                ) ||
                !geometryType ||
                !title ||
                !layerId
              }
              sx={blueButtonStyle}
              startIcon={<UploadIcon />}
            >
              Upload
            </Button>
            <Button variant="contained" onClick={handleCancelClick} sx={whiteButtonStyle} startIcon={<CancelIcon />}>
              Cancel
            </Button>
          </>
        );
      case 2:
        return (
          <>
            <Button
              variant="contained"
              onClick={handlePreviousClick}
              sx={whiteButtonStyle}
              startIcon={<NavigateBeforeIcon />}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadClick}
              autoFocus
              disabled={
                !shpFile ||
                !["application/zip", "application/octet-stream", "application/x-zip-compressed"].includes(
                  shpFile.type
                ) ||
                !shpLayerFile ||
                !title ||
                !layerId
              }
              sx={blueButtonStyle}
              startIcon={<UploadIcon />}
            >
              Upload
            </Button>
            <Button variant="contained" onClick={handleCancelClick} sx={whiteButtonStyle} startIcon={<CancelIcon />}>
              Cancel
            </Button>
          </>
        );
      case 3:
        return (
          <>
            <Button
              variant="contained"
              onClick={handlePreviousClick}
              sx={whiteButtonStyle}
              startIcon={<NavigateBeforeIcon />}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadClick}
              autoFocus
              disabled={
                !geoFile ||
                ![
                  "image/apng",
                  "image/avif",
                  "image/gif",
                  "image/jpeg",
                  "image/png",
                  "image/svg+xml",
                  "image/webp",
                  "image/bmp",
                  "image/tiff",
                ].includes(geoFile.type) ||
                !xMin ||
                !yMin ||
                !xMax ||
                !yMax ||
                !title ||
                !layerId
              }
              sx={blueButtonStyle}
              startIcon={<UploadIcon />}
            >
              Upload
            </Button>
            <Button variant="contained" onClick={handleCancelClick} sx={whiteButtonStyle} startIcon={<CancelIcon />}>
              Cancel
            </Button>
          </>
        );
      case 4:
        return (
          <>
            <Button
              variant="contained"
              onClick={handlePreviousClick}
              sx={whiteButtonStyle}
              startIcon={<NavigateBeforeIcon />}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadClick}
              autoFocus
              disabled={
                !geoFile ||
                ![
                  "image/apng",
                  "image/avif",
                  "image/gif",
                  "image/jpeg",
                  "image/png",
                  "image/svg+xml",
                  "image/webp",
                  "image/bmp",
                  "image/tiff",
                ].includes(geoFile.type) ||
                !geoWorldFile ||
                !title ||
                !layerId
              }
              sx={blueButtonStyle}
              startIcon={<UploadIcon />}
            >
              Upload
            </Button>
            <Button variant="contained" onClick={handleCancelClick} sx={whiteButtonStyle} startIcon={<CancelIcon />}>
              Cancel
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (isOpen && !showDialog) {
      setPage(0);
      setFileType("shp");
      setShpFile(null);
      setShpLayerFile(null);
      setGeoFile(null);
      setGeoWorldFile(null);
      setGeometryType("");
      setTitle(null);
      setLayerId(null);
      setLayerIdError(null);
      setCopyright(null);
      setDisplayInList(true);
      setMaxScale(0);
      setMinScale(0);
      setOpacity(1);
      setEsuSnap(false);
      setBlpuSnap(false);
      setExtentSnap(false);
      setXMin(0);
      setYMin(0);
      setXMax(0);
      setYMax(0);
    }

    if (isOpen !== showDialog) setShowDialog(isOpen);
  }, [isOpen, showDialog]);

  return (
    <div>
      <Dialog open={showDialog} aria-labelledby="message-dialog" fullWidth maxWidth="md" onClose={handleCancelClick}>
        <ADSDialogTitle title={getTitle()} closeTooltip="Cancel" onClose={handleCancelClick} />
        <DialogContent sx={{ mt: theme.spacing(2) }}>{getContent()}</DialogContent>
        <DialogActions sx={{ justifyContent: "flex-start", mb: theme.spacing(1), ml: theme.spacing(2.25) }}>
          {getActions()}
        </DialogActions>
      </Dialog>
      <MinMaxDialog
        isOpen={showMinMaxDialog}
        variant={minMaxType}
        minValue={minValue.current}
        maxValue={maxValue.current}
        maximum={maximum}
        onNewMinMax={(data) => handleNewMinMax(data)}
        onClose={handleCloseMinMax}
      />
    </div>
  );
}

export default UploadShpFileDialog;
